import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { api } from "../api";
import "./AdminPanel.css";
import "./Dashboard.css";

const MONTHS_SHORT = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const MONTH_KEYS = ["apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "decm", "jan", "feb", "mar"];

interface OverviewAccount { account: string; category: string; total: number; months: Record<string, number>; }
interface OverviewResult {
  cost_center: string; cost_center_name: string; dimension: string;
  grand_total: number; accounts: OverviewAccount[];
}

const CAT_COLOR = (cat: string): string => ({
  COMPENSATION: "#4A90E2", OCCUPANCY: "#e84393", OPERATING: "#ea580c",
  ADVERTISING: "#7c3aed", ALLOCATED: "#475569", AMORT: "#64748b",
} as Record<string, string>)[cat.toUpperCase().replace(/\s/g, "")] ?? "#94a3b8";

const EXCLUDED_CATS = new Set(["Corp OH", "CORP_OH", "Uncategorized", "OtherAdjustments", "OTHERADJUSTMENTS"]);

function fmtMoney(n: number): string {
  const safe = Number.isFinite(n) ? n : 0;
  const formatted = "$" + Math.round(Math.abs(safe)).toLocaleString("en-US");
  return safe < 0 ? `(${formatted})` : formatted;
}
function zeroMonths(): Record<string, number> {
  return Object.fromEntries(MONTH_KEYS.map(k => [k, 0]));
}

// Sort dimensions in actual fiscal order rather than alphabetically.
const DIM_TYPE_ORDER: Record<string, number> = {
  "annual budget": 0, "july forecast": 1, "october forecast": 2, "january forecast": 3,
};
function sortDims(dims: string[]): string[] {
  const parse = (d: string) => {
    const fyMatch = d.match(/FY(\d+)/i);
    const fy = fyMatch ? parseInt(fyMatch[1], 10) : 0;
    const roundMatch = d.match(/Planning Round (\d+)/i);
    if (roundMatch) return { fy, rank: 4 + parseInt(roundMatch[1], 10) };
    const typeKey = d.toLowerCase().replace(/\s*fy\d+/i, "").trim();
    return { fy, rank: DIM_TYPE_ORDER[typeKey] ?? 99 };
  };
  return [...dims].sort((a, b) => {
    const pa = parse(a), pb = parse(b);
    return pa.fy !== pb.fy ? pa.fy - pb.fy : pa.rank - pb.rank;
  });
}

interface Props {
  costCenterNumber: string;
  costCenterName?: string;
  // "cc" = this one cost center, "team" = rolled up across every CC the
  // user leads. Controlled from App.tsx, which owns the My Team pill
  // alongside the CC selector, so picking a CC always clears team mode.
  scope?: "cc" | "team";
}

export default function BudgetOverviewView({ costCenterNumber, scope = "cc" }: Props) {
  const [dims, setDims] = useState<string[]>([]);
  const [dimension, setDimension] = useState("");
  const [result, setResult] = useState<OverviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCat, setExpandedCat] = useState<string | null>(null);

  // Account filter — multi-select search combo, same pattern as the admin
  // Budget Overview's "All Accounts" picker.
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [acctOpen, setAcctOpen] = useState(false);
  const [acctSearch, setAcctSearch] = useState("");
  const acctRef = useRef<HTMLDivElement>(null);
  const [simplifiedView, setSimplifiedView] = useState(false);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (acctRef.current && !acctRef.current.contains(e.target as Node)) setAcctOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (scope === "cc" && !costCenterNumber) return;
    setResult(null);
    const req = scope === "team"
      ? api.get("/budgets/team-compare/dimensions")
      : api.get("/budgets/compare/dimensions", { params: { cc: costCenterNumber } });
    req
      .then(res => {
        const list: string[] = sortDims(res.data.dimensions || []);
        setDims(list);
        setDimension(list[list.length - 1] || "");
      })
      .catch(() => setDims([]));
  }, [costCenterNumber, scope]);

  const loadOverview = useCallback((dim: string) => {
    if (!dim) return;
    setLoading(true);
    setError(null);
    setSelectedAccounts([]);
    const req = scope === "team"
      ? api.get("/budgets/team-overview", { params: { dimension: dim } })
      : api.get("/budgets/overview", { params: { cc: costCenterNumber, dimension: dim } });
    req
      .then(res => setResult(res.data))
      .catch(err => setError(err?.response?.data?.error || "Failed to load budget overview"))
      .finally(() => setLoading(false));
  }, [costCenterNumber, scope]);

  useEffect(() => {
    if (dimension) loadOverview(dimension);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimension, costCenterNumber, scope]);

  const allAccounts = useMemo(
    () => (result?.accounts ?? []).filter(a => !EXCLUDED_CATS.has(a.category)),
    [result]
  );

  // Visible accounts honor the account filter and, when simplifiedView is on,
  // drop any account whose value is $0 across every month for this dimension.
  // Categories are re-derived from the result so the KPI strip / chart /
  // table all stay in sync with both filters automatically.
  const visAccounts = (selectedAccounts.length === 0
    ? allAccounts
    : allAccounts.filter(a => selectedAccounts.includes(a.account))
  ).filter(a => !simplifiedView || Math.round(a.total) !== 0);

  const visCategories = useMemo(() => {
    const map: Record<string, { category: string; total: number; months: Record<string, number> }> = {};
    visAccounts.forEach(a => {
      if (!map[a.category]) map[a.category] = { category: a.category, total: 0, months: zeroMonths() };
      map[a.category].total += a.total;
      MONTH_KEYS.forEach(k => { map[a.category].months[k] += a.months[k] || 0; });
    });
    const CATEGORY_ORDER = ["COMPENSATION", "OCCUPANCY", "OPERATING", "ADVERTISING", "ALLOCATED", "AMORT"];
    return Object.values(map).sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a.category);
      const bi = CATEGORY_ORDER.indexOf(b.category);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [visAccounts]);

  const visTotal = visCategories.reduce((s, c) => s + c.total, 0);

  const chartData = MONTHS_SHORT.map((label, i) => {
    const row: Record<string, number | string> = { month: label };
    visCategories.forEach(c => { row[c.category] = c.months[MONTH_KEYS[i]] || 0; });
    return row;
  });

  return (
    <div className="admin-section" style={{ margin: "16px 0" }}>
      <div className="admin-section-header">
        <span className="material-icons">account_balance_wallet</span>
        <span className="admin-section-title">Budget Overview</span>
      </div>
      <div className="admin-section-body">
        <div className="fc-toolbar">
          <div className="fc-toolbar-selectors">
            <div className="fc-dim-group">
              <span className="fc-dim-group-label">Dimension</span>
              <select className="fc-dim-select" value={dimension} onChange={e => setDimension(e.target.value)}>
                {dims.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            {/* Account multi-select combo — same pattern as the admin Budget Overview's "All Accounts" picker */}
            <div className="bo-acct-combo" ref={acctRef}>
              <button className="bo-acct-trigger" onClick={() => setAcctOpen(p => !p)}>
                {selectedAccounts.length === 0 ? "All Accounts" : `${selectedAccounts.length} account${selectedAccounts.length > 1 ? "s" : ""}`}
                <span className="material-icons" style={{ fontSize: 16 }}>expand_more</span>
              </button>
              {acctOpen && (
                <div className="bo-acct-dropdown">
                  <input className="bo-acct-search" placeholder="Search accounts…"
                    value={acctSearch} onChange={e => setAcctSearch(e.target.value)} autoFocus />
                  {selectedAccounts.length > 0 && (
                    <button className="bo-acct-clear" onClick={() => { setSelectedAccounts([]); setAcctSearch(""); }}>
                      Clear all
                    </button>
                  )}
                  <div className="bo-acct-list">
                    {allAccounts
                      .filter(a => !acctSearch || a.account.toLowerCase().includes(acctSearch.toLowerCase()))
                      .map(a => (
                        <label key={a.account} className="bo-acct-option">
                          <input type="checkbox"
                            checked={selectedAccounts.includes(a.account)}
                            onChange={() => setSelectedAccounts(prev =>
                              prev.includes(a.account) ? prev.filter(x => x !== a.account) : [...prev, a.account]
                            )} />
                          <span className="bo-acct-option-label">{a.account}</span>
                          <span className="bo-acct-option-cat">{a.category}</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="fc-toolbar-actions">
            <button
              onClick={() => setSimplifiedView(v => !v)}
              className={`excel-mode-btn excel-mode-btn--simplified${simplifiedView ? " excel-mode-btn--active" : ""}`}
              data-tooltip={simplifiedView ? "Show All — show all accounts including $0 lines" : "Simplified View — hides accounts with $0 across every month"}
              aria-label={simplifiedView ? "Show All" : "Simplified View"}
            >
              <span className="material-icons">{simplifiedView ? "visibility_off" : "visibility"}</span>
            </button>
            <button className="fc-refresh-btn" onClick={() => loadOverview(dimension)}>
              <span className="material-icons">refresh</span>
            </button>
          </div>
        </div>

        {result?.cost_center_name && (
          <div style={{ fontSize: 12, fontWeight: 600, color: "oklch(0.5 0.02 250)", margin: "-10px 0 14px" }}>
            {scope === "team" ? "Rolled up across " : "Scoped to "}{result.cost_center_name}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 24, color: "#9ca3af", fontSize: 13 }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 24, color: "oklch(0.5 0.18 25)", fontSize: 13 }}>{error}</div>
        ) : !result ? (
          <div style={{ padding: 24, color: "#9ca3af", fontSize: 13 }}>
            {dims.length === 0 ? "No budget data found yet for this cost center." : "Pick a dimension to view."}
          </div>
        ) : (
          <>
            <div className="dash-kpi-strip" style={{ marginTop: 16 }}>
              <div className="dash-kpi-card">
                <span className="dash-kpi-label">Total Budget</span>
                <span className="dash-kpi-value">{fmtMoney(visTotal)}</span>
              </div>
              {visCategories.map(c => (
                <div className="dash-kpi-card" key={c.category}>
                  <span className="dash-kpi-label" style={{ color: CAT_COLOR(c.category) }}>{c.category}</span>
                  <span className="dash-kpi-value">{fmtMoney(c.total)}</span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 20, height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => fmtMoney(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {visCategories.map(c => (
                    <Bar key={c.category} dataKey={c.category} stackId="a" fill={CAT_COLOR(c.category)} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={{ overflowX: "auto", marginTop: 20 }}>
              <table className="fv-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    {MONTHS_SHORT.map(m => <th key={m} className="fv-th-right">{m}</th>)}
                    <th className="fv-th-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {visCategories.map(c => (
                    <React.Fragment key={c.category}>
                      <tr style={{ cursor: "pointer" }} onClick={() => setExpandedCat(expandedCat === c.category ? null : c.category)}>
                        <td style={{ fontWeight: 700 }}>
                          <span className="material-icons" style={{ fontSize: 16, verticalAlign: "middle", transform: expandedCat === c.category ? "rotate(90deg)" : "none", transition: "transform .15s" }}>chevron_right</span>
                          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: CAT_COLOR(c.category), margin: "0 6px" }} />
                          {c.category}
                        </td>
                        {MONTH_KEYS.map(k => <td key={k} className="fv-amount">{fmtMoney(c.months[k])}</td>)}
                        <td className="fv-amount">{fmtMoney(c.total)}</td>
                      </tr>
                      {expandedCat === c.category && visAccounts.filter(a => a.category === c.category).map(a => (
                        <tr key={a.account} style={{ background: "oklch(0.98 0.003 250)" }}>
                          <td style={{ paddingLeft: 36, fontSize: 12, color: "oklch(0.4 0.02 250)" }}>{a.account}</td>
                          {MONTH_KEYS.map(k => <td key={k} className="fv-amount" style={{ fontSize: 12 }}>{fmtMoney(a.months[k])}</td>)}
                          <td className="fv-amount" style={{ fontSize: 12 }}>{fmtMoney(a.total)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 700, borderTop: "2px solid oklch(0.9 0.01 250)" }}>
                    <td>Total</td>
                    {MONTH_KEYS.map(k => (
                      <td key={k} className="fv-amount">{fmtMoney(visCategories.reduce((s, c) => s + (c.months[k] || 0), 0))}</td>
                    ))}
                    <td className="fv-amount">{fmtMoney(visTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
